import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';
import { format, parseISO } from 'date-fns';

const { FiCheckSquare, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiUser, FiClock, FiAlertTriangle } = FiIcons;

const TasksManagement = () => {
  const { data, addItem, updateItem, deleteItem } = useAuth0Data();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    assignee: '',
    dueDate: '',
    status: 'pending',
    priority: 'medium'
  });

  // Get all tasks from projects since tasks are stored within projects
  const getAllTasks = () => {
    return data.projects.flatMap(project =>
      (project.tasks || []).map(task => ({
        ...task,
        projectTitle: project.title,
        projectId: project.id
      }))
    );
  };

  const allTasks = getAllTasks();

  const handleAdd = () => {
    if (formData.title.trim() && formData.assignee.trim() && formData.dueDate) {
      // For now, we'll just show a message that tasks should be created through projects
      alert('Tasks should be created through Project Management. This is for viewing existing tasks only.');
      handleCancel();
    }
  };

  const handleEdit = (task) => {
    setEditingId(task.id);
    setFormData({
      title: task.title,
      assignee: task.assignee,
      dueDate: task.dueDate,
      status: task.status,
      priority: task.priority || 'medium'
    });
  };

  const handleUpdate = () => {
    // Find the project that contains this task and update it
    const project = data.projects.find(p => 
      (p.tasks || []).some(t => t.id === editingId)
    );
    
    if (project) {
      const updatedTasks = (project.tasks || []).map(task =>
        task.id === editingId 
          ? { ...task, ...formData }
          : task
      );
      updateItem('projects', project.id, { ...project, tasks: updatedTasks });
    }
    
    setEditingId(null);
    setFormData({
      title: '',
      assignee: '',
      dueDate: '',
      status: 'pending',
      priority: 'medium'
    });
  };

  const handleDelete = (task) => {
    if (confirm('Are you sure you want to delete this task?')) {
      const project = data.projects.find(p => 
        (p.tasks || []).some(t => t.id === task.id)
      );
      
      if (project) {
        const updatedTasks = (project.tasks || []).filter(t => t.id !== task.id);
        updateItem('projects', project.id, { ...project, tasks: updatedTasks });
      }
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({
      title: '',
      assignee: '',
      dueDate: '',
      status: 'pending',
      priority: 'medium'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckSquare;
      case 'overdue': return FiAlertTriangle;
      default: return FiClock;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'todo': return 'text-blue-600 bg-blue-100';
      case 'in-progress': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const pendingTasks = allTasks.filter(task => 
    task.status === 'todo' || task.status === 'in-progress'
  ).length;
  
  const overdueTasks = allTasks.filter(task => 
    task.status === 'overdue'
  ).length;
  
  const completedTasks = allTasks.filter(task => 
    task.status === 'completed'
  ).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiCheckSquare} className="w-5 h-5 text-indigo-600" />
          </div>
          <h3 className="ml-3 text-xl font-bold text-gray-900">Tasks Overview</h3>
        </div>
        <div className="text-sm text-gray-500">
          Tasks are managed through Project Management
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-xl font-bold text-blue-600">{pendingTasks}</p>
        </div>
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <p className="text-sm text-gray-600">Overdue</p>
          <p className="text-xl font-bold text-red-600">{overdueTasks}</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-xl font-bold text-green-600">{completedTasks}</p>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {allTasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiCheckSquare} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No tasks found.</p>
            <p className="text-sm mt-2">
              Create projects and add tasks in{' '}
              <span className="text-primary-600 font-medium">Project Management</span>
            </p>
          </div>
        ) : (
          allTasks.map((task, index) => (
            <motion.div
              key={`${task.id}-${task.projectId}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 rounded-lg p-4"
            >
              {editingId === task.id ? (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                      <input
                        type="text"
                        value={formData.assignee}
                        onChange={(e) => setFormData(prev => ({ ...prev, assignee: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="todo">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="overdue">Overdue</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdate}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(task.status)}`}>
                        <SafeIcon icon={getStatusIcon(task.status)} className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {task.title}
                        </h4>
                        <div className="flex items-center gap-4 mt-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                            {task.assignee}
                          </div>
                          {task.dueDate && (
                            <div className="flex items-center text-sm text-gray-600">
                              <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                              {format(parseISO(task.dueDate), 'MMM dd, yyyy')}
                            </div>
                          )}
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                            {task.projectTitle}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(task)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(task)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Help Text */}
      {allTasks.length === 0 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">How to create tasks:</h4>
          <ol className="text-sm text-blue-800 space-y-1">
            <li>1. Go to <strong>Project Management</strong></li>
            <li>2. Create a new project or open an existing one</li>
            <li>3. Add tasks to your project</li>
            <li>4. Tasks will appear here for overview and quick editing</li>
          </ol>
        </div>
      )}
    </motion.div>
  );
};

export default TasksManagement;